import { useEffect, useId } from 'react';
import { AssistantPageState } from './assistantContextTypes';
import { useAssistantContext } from './AssistantContextProvider';

/**
 * Publish a page's assistant context for as long as the page is mounted.
 *
 * The owner id is per-hook-instance, so unmounting clears exactly this page's
 * context and cannot wipe whatever mounted after it.
 *
 * `state` should be memoized by the caller; this hook republishes whenever the
 * reference changes, and the provider drops the update if the normalized
 * snapshot is identical.
 */
export function useAssistantPageContext(state: AssistantPageState | null) {
  const ownerId = useId();
  const { publish, clear } = useAssistantContext();

  useEffect(() => {
    publish(ownerId, state);
  }, [ownerId, publish, state]);

  useEffect(() => () => clear(ownerId), [ownerId, clear]);
}

export default useAssistantPageContext;
